import apiClient from './api-client';

export interface DPOptionOption {
  optionId?: number;
  optionValue: string;
  optionDescription?: string;
  isDefaultSelected?: boolean;
  displayOrder?: number;
  translations?: {
    [key: string]: {
      translatedOptionValue: string;
      translatedOptionDescription: string | null;
    };
  };
}

export interface DPOption {
  id: number;
  name: string;
  description?: string;
  isMandatory?: boolean;
  languageCode: string;
  displayAs: 'BUTTON' | 'CHECKBOX';
  allowMultipleSelection?: boolean;
  setADefaultSelection?: boolean;
  options: DPOptionOption[];
  createdDate: string;
  lastModified: string;
  isActive: boolean;
  isPublished: boolean;
  translations: {
    [key: string]: {
      translatedName: string;
      translatedDescription: string;
    };
  };
}

export interface NewDPOption {
  name: string;
  description?: string;
  isMandatory?: boolean;
  languageCode: string;
  displayAs: 'BUTTON' | 'CHECKBOX';
  allowMultipleSelection?: boolean;
  setADefaultSelection?: boolean;
  options: Omit<DPOptionOption, 'optionId'>[];
}

export interface DPOptionQueryParams {
    filters?: {
        isPublished?: boolean;
        isMandatory?: boolean;
        displayAs?: 'BUTTON' | 'CHECKBOX';
    };
    pagination: {
        page: number;
        pageSize: number;
    };
    sortBy: 'A-Z' | 'Z-A' | 'newest' | 'oldest' | 'Newest' | 'Oldest' | 'a-z' | 'z-a';
}

export interface DPOptionTranslation {
  name: string;
  description: string;
  options: {
    optionId: number;
    translatedValue: string;
  }[];
}

export interface DPOptionResponse {
    status: 'success';
    data: DPOption;
}

export interface DPOptionsPaginatedResponse {
    status: 'success';
    data: DPOption[];
    pagination: {
        total: number;
        page: number;
        pageSize: number;
        totalPages: number;
    };
}

export interface PublishReadinessCheck {
    isReady: boolean;
    statusCode: 'READY' | 'NOT_READY';
    discrepancies: {
        missingOptions?: boolean;
        missingTranslations?: {
            languageCode: string;
            languageName: string;
        }[];
    };
}

// API Path Constants
const API_BASE = '/masterdata/preferences';
const API_PATHS = {
    BASE: API_BASE,
    BY_ID: (id: number) => `${API_BASE}/${id}`,
    PUBLISH: (id: number) => `${API_BASE}/${id}/publish`,
    STATUS: (id: number) => `${API_BASE}/${id}/status`,
    CHECK_PUBLISH_READINESS: (id: number) => `${API_BASE}/${id}/publish-readiness`,
} as const;


export const dpOptionsService = {
  getDPOptions: async (queryParams?: DPOptionQueryParams): Promise<DPOptionsPaginatedResponse> => {
    const params = new URLSearchParams();
    
    if (queryParams?.filters) {
      if (queryParams.filters.isPublished !== undefined) {
        params.append('isPublished', queryParams.filters.isPublished.toString());
      }
      if (queryParams.filters.isMandatory !== undefined) {
        params.append('isMandatory', queryParams.filters.isMandatory.toString());
      }
      if (queryParams.filters.displayAs) {
        params.append('displayAs', queryParams.filters.displayAs);
      }
    }

    if (queryParams?.pagination) {
      params.append('page', queryParams.pagination.page.toString());
      params.append('pageSize', queryParams.pagination.pageSize.toString());
    }

    if (queryParams?.sortBy) {
      params.append('sortBy', queryParams.sortBy);
    }

    const response = await apiClient.get<DPOptionsPaginatedResponse>(`${API_PATHS.BASE}?${params.toString()}`);
    return response;
  },

  getDPOptionById: async (id: number): Promise<DPOptionResponse> => {
    const response = await apiClient.get<DPOptionResponse>(API_PATHS.BY_ID(id));
    return response;
  },

  createDPOption: async (option: NewDPOption): Promise<DPOption> => {
    const response = await apiClient.post<{ success: boolean; data: DPOption }>(API_PATHS.BASE, option);
    return response.data;
  },

  updateDPOption: async (id: number, option: Partial<DPOption>): Promise<DPOption> => {
    console.log('Updating DPOption:', option);
    const response = await apiClient.put<{ success: boolean; data: DPOption }>(API_PATHS.BY_ID(id), option);
    return response.data;
  },

  deleteDPOption: async (id: number): Promise<void> => {
    await apiClient.delete<{ success: boolean; message: string }>(API_PATHS.BY_ID(id));
  },

  updateDPOptionStatus: async (id: number, status: boolean): Promise<DPOption> => {
    const response = await apiClient.patch<{ success: boolean; data: DPOption }>(
      API_PATHS.STATUS(id),
      { isActive: status }
    );
    return response.data;
  },

  publishDPOption: async (id: number): Promise<DPOption> => {
    const response = await apiClient.patch<{ success: boolean; data: DPOption }>(
      API_PATHS.PUBLISH(id),
      { isPublished: true }
    );
    return response.data;
  },

  unpublishDPOption: async (id: number): Promise<DPOption> => {
    const response = await apiClient.patch<{ success: boolean; data: DPOption }>(
      API_PATHS.PUBLISH(id),
      { isPublished: false }
    );
    return response.data;
  },

  saveDPOptionTranslation: async (id: number, languageCode: string, name: string, description: string, options: { optionId: number; translatedValue: string }[]): Promise<DPOption> => {
    const response = await apiClient.put<{ success: boolean; data: DPOption }>(
      `${API_PATHS.BY_ID(id)}/translations`,
      {
        purposePreferenceId: id,
        languageCode,
        translatedName: name,
        translatedDescription: description,
        options: options.map(opt => ({
          optionId: opt.optionId,
          translatedOptionValue: opt.translatedValue
        }))
      }
    );
    return response.data;
  },

  checkPublishReadiness: async (id: number): Promise<PublishReadinessCheck> => {
    const response = await apiClient.get<PublishReadinessCheck>(API_PATHS.CHECK_PUBLISH_READINESS(id));
    return response;
  },
}; 